/**
 * Supabase Client Configuration
 * 
 * This file initializes the Supabase client for the Bench Bulletin application.
 * It uses environment variables to securely connect to your Supabase project.
 * 
 * SECURITY NOTES:
 * - VITE_SUPABASE_ANON_KEY is safe to expose to the client (it respects RLS policies)
 * - NEVER expose SUPABASE_SERVICE_ROLE_KEY to the client (use only in Edge Functions)
 */

import { createClient } from '@supabase/supabase-js'

// Validate required environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. ' +
    'Please check that VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set in your .env.local file.'
  )
}

/**
 * Main Supabase client for use throughout the application.
 * This client respects Row Level Security (RLS) policies.
 */
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Store session in localStorage for persistence
    persistSession: true,
    // Automatically refresh tokens
    autoRefreshToken: true,
    // Detect when session is missing
    detectSessionInUrl: true,
  },
  db: {
    schema: 'public',
  },
  // Additional configuration
  global: {
    headers: {
      'x-application-name': 'bench-bulletin',
    },
  },
})

/**
 * Helper function to check if user is authenticated
 * @returns {Promise<boolean>}
 */
export async function isAuthenticated() {
  const { data: { session } } = await supabase.auth.getSession()
  return !!session
}

/**
 * Helper function to get current user
 * @returns {Promise<User | null>}
 */
export async function getCurrentUser() {
  const { data: { user }, error } = await supabase.auth.getUser()
  if (error) {
    console.error('Error getting current user:', error)
    return null
  }
  return user
}

/**
 * Helper function to sign out
 * @returns {Promise<void>}
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut()
  if (error) {
    console.error('Error signing out:', error)
    throw error
  }
}

/**
 * Storage bucket helper for file uploads
 * @param {string} bucketName - Name of the storage bucket
 */
export function getStorageBucket(bucketName) {
  return supabase.storage.from(bucketName)
}

/**
 * Realtime subscription helper
 * @param {string} table - Table name to subscribe to
 * @param {Function} callback - Callback function for changes
 */
export function subscribeToTable(table, callback) {
  return supabase
    .channel(`public:${table}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: table,
      },
      callback
    )
    .subscribe()
}

// Export the client as default
export default supabase


